import React, { useRef, useState } from "react";

const STEP = 32;

/** Sizes the pane after it: dragged, or stepped with the arrow keys. The
 *  layout clamps the width, so each move starts from the pane as drawn. */
export const PaneDivider: React.FC<{
  label: string;
  onResize: (width: number) => void;
  /** Where the line starts, to run alongside the panes' boxes rather than their headings. */
  top?: string;
}> = ({ label, onResize, top }) => {
  const from = useRef<{ x: number; width: number } | null>(null);
  const [dragging, setDragging] = useState(false);
  const drawn = (el: HTMLElement) =>
    el.nextElementSibling?.getBoundingClientRect().width ?? 0;
  const release = () => {
    from.current = null;
    setDragging(false);
  };
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      tabIndex={0}
      data-dragging={dragging || undefined}
      className="resizable-divider hidden lg:block"
      style={top ? { marginTop: top } : undefined}
      onPointerDown={(e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        from.current = { x: e.clientX, width: drawn(e.currentTarget) };
        setDragging(true);
      }}
      onPointerMove={(e) => {
        if (from.current)
          onResize(
            Math.max(
              0,
              Math.round(from.current.width + from.current.x - e.clientX),
            ),
          );
      }}
      onPointerUp={release}
      onLostPointerCapture={release}
      onKeyDown={(e) => {
        const step =
          e.key === "ArrowLeft" ? STEP : e.key === "ArrowRight" ? -STEP : 0;
        if (!step) return;
        e.preventDefault();
        onResize(Math.max(0, Math.round(drawn(e.currentTarget) + step)));
      }}
    />
  );
};
